import { cva, type VariantProps } from 'class-variance-authority';
import { format } from 'date-fns/format';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { ReferralCta } from './referral-cta';
import { useSubscription } from './subscription.query';
import { subscriptionStore } from './subscription.store';

import type { ReactNode } from 'react';

/** The App Store routes subscription management through the iOS app */
export function AppStorePlanNotice({
  copyClassName,
}: {
  copyClassName?: string;
}) {
  return (
    <p className={copyClassName}>
      Your plan is billed through the App Store. Manage it in the Littlebird iOS
      app.
    </p>
  );
}

// CalloutBox - inline alert for use within settings cards

const calloutVariants = cva(
  'py-3 px-4 border rounded-lg items-center justify-between gap-4',
  {
    variants: {
      variant: {
        info: 'bg-blue-500/5 dark:bg-blue-500/10 border-blue-500/20',
        warning:
          'bg-amber-500/5 dark:bg-amber-500/10 border-amber-600/30 dark:border-amber-500/30',
        danger: 'bg-red-500/5 dark:bg-red-500/10 border-red-500/20',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const calloutTextVariants = cva('text-sm', {
  variants: {
    variant: {
      info: 'text-blue-900 dark:text-blue-50',
      warning: 'text-amber-900 dark:text-amber-50',
      danger: 'text-red-900 dark:text-red-50',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

export type CalloutBoxProps = {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
} & VariantProps<typeof calloutVariants>;

export function CalloutBox({
  variant,
  title,
  description,
  action,
  className,
}: CalloutBoxProps) {
  return (
    <XStack className={cn(calloutVariants({ variant }), className)}>
      <YStack className="gap-1 flex-1">
        <p className={cn(calloutTextVariants({ variant }), 'font-semibold')}>
          {title}
        </p>
        {description && (
          <div className={calloutTextVariants({ variant })}>{description}</div>
        )}
      </YStack>
      {action && <div className="flex-shrink-0">{action}</div>}
    </XStack>
  );
}

// Global floating notifications that appear at the top of the screen

export const PaywallNotification = observer(() => {
  const paywallBanner = uiStore.paywallBanner;
  const { setHash } = useHashRoute();
  const isPlus = subscriptionStore.isPlus;

  if (!paywallBanner.show) return null;

  const subtitle =
    paywallBanner.subtitle ||
    'This feature requires a subscription. Upgrade to continue.';
  const onClose = () => uiStore.hidePaywallBanner();

  return (
    <YStack className="fixed top-10 left-4 right-4 z-50 mx-auto max-w-2xl gap-1.5">
      {isPlus ? (
        <Banner
          variant="default"
          showIcon
          title="Daily limit reached"
          subtitle={subtitle}
          onClose={onClose}
        />
      ) : (
        <>
          <Banner
            variant="default"
            showIcon
            title={paywallBanner.title || 'Upgrade Required'}
            subtitle={subtitle}
            primaryActionText="Upgrade"
            onPrimaryAction={() => {
              setHash('settings/Subscription');
              uiStore.hidePaywallBanner();
            }}
            onClose={onClose}
          />
          <ReferralCta
            source="paywall_notification"
            className="block text-center w-full"
          />
        </>
      )}
    </YStack>
  );
});

export const TrialEndingNotification = observer(
  ({ className }: { className?: string }) => {
    const { createPortalSession } = useSubscription();
    const daysLeft = subscriptionStore.noCardTrialDaysLeft;
    const trialEnd = subscriptionStore.state.currentPeriodEnd;

    if (
      daysLeft === null ||
      daysLeft > 3 ||
      !trialEnd ||
      uiStore.trialEndingBannerDismissedOn === new Date().toDateString()
    ) {
      return null;
    }

    return (
      <div className={className}>
        <Banner
          variant="default"
          showIcon
          title={
            daysLeft === 0
              ? 'Your Plus trial ends today.'
              : `Your Plus trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}.`
          }
          subtitle={`Add a card to keep Plus, or you'll move to Basic on ${format(new Date(trialEnd * 1000), 'MMMM d')}.`}
          primaryActionText="Add card"
          onPrimaryAction={() => createPortalSession({ add_card: true })}
          onClose={() => uiStore.dismissTrialEndingBanner()}
        />
      </div>
    );
  }
);

export const PaymentFailedNotification = observer(
  ({ className }: { className?: string }) => {
    const { status, isLoading, createPortalSession, isCreatingPortal } =
      useSubscription();

    if (
      isLoading ||
      status?.subscription_status !== 'past_due' ||
      uiStore.pastDueBannerDismissed
    ) {
      return null;
    }

    const handleFixPayment = async () => {
      await createPortalSession();
    };

    return (
      <div className={className}>
        <Banner
          title="Payment Failed"
          subtitle="We couldn't process your payment. Please update your payment method to avoid service interruption."
          variant="danger"
          className="bg-danger-9 border-danger-10 dark:bg-danger-9"
          actions={
            <XStack className="gap-3 items-center">
              <Button
                onClick={handleFixPayment}
                size="sm"
                isLoading={isCreatingPortal}
                disabled={isCreatingPortal}
                className="min-w-[100px] bg-white text-danger-9 hover:bg-white"
              >
                Fix Payment
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => uiStore.setPastDueBannerDismissed(true)}
                className="rounded-full text-white hover:bg-white/20"
              >
                <Icons.cancel className="size-4" />
              </Button>
            </XStack>
          }
        />
      </div>
    );
  }
);
